import React, { Fragment, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import DotsVerticalIcon from 'mdi-react/DotsVerticalIcon';
import ChevronDown from 'mdi-react/ChevronDownIcon';
import Panel from '@prisjakt/ui-panel';
import graphqlPropTypes from '@prisjakt/graphql-proptypes';
import { AppContext } from '@prisjakt/ui-app-context';
import PanelHeader from '@prisjakt/ui-panel-header';
import T from '@prisjakt/ui-translate';
import Button from '@prisjakt/ui-button';
import Dropdown from '@prisjakt/ui-dropdown';
import { bemGenerator } from '@prisjakt/ui-base';
import { Link } from 'react-router-dom';

import Lazy from '@prisjakt/ui-lazy';
import PriceInformation from '../price-information';
import Placeholder from './placeholder';
import PriceFilters from './price-filters/price-filters';
import QuickPriceFilters from './quick-price-filters/quick-price-filters';
import AdContainer from '../../../../components/ads';
import PriceRow, { columnCount } from '../../../../components/price-row';
import {
  productPriceClick,
  productClickOutBounce,
} from '../../actions/product';
import './index.scss';
import * as S from './styles';
import { PriceListFragment } from '../../../../types';
import {
  useNonFeaturedStoreLinkExperiment,
  useShowStoreProductNamesExperiment,
} from '../../../../hooks/experiments';

const styles = bemGenerator('pj-ui-pricelist', [
  'transparency-info',
  'transparency-info-short',
  'transparency-info-read-more',
  'header',
  'rating-link',
  'link',
  'no-prices',
  'advert',
  'price-filter',
  'price-quick-filter',
  'price-filter-toggle',
  'only-mobile',
  'only-desktop',
  'related-products-inline',
  'information',
]);

class PriceList extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      showMorePrices: false,
      showUsedProducts: false,
      showInternational: false,
      showNotInMintCondition: false,
      showPickupOnly: true,
      showUnavailable: false,
      featuredAlternativePriceType: 'international',
      hiddenListOptions: [],
      // The market can override the default display settings, e.g. to always
      // show international prices.
      ...this.props.appContext.market.priceList,
    };

    const filteredPrices = this.getPrices();
    if (filteredPrices && filteredPrices.length === 0) {
      // If no prices are visible, include used products
      this.state.showUsedProducts = true;
    }

    this.renderPriceRow = this.renderPriceRow.bind(this);
    this.handlePriceFilterCheckbox = this.handlePriceFilterCheckbox.bind(this);
  }

  onMorePrices() {
    this.setState({
      showMorePrices: true,
    });
  }

  getPrices() {
    if (!this.props.prices.nodes) {
      return null;
    }

    const {
      showInternational,
      showNotInMintCondition,
      showUnavailable,
      showUsedProducts,
      showPickupOnly,
    } = this.state;

    return this.props.prices.nodes.filter(price => {
      // Check first if the store's market is allowed
      if (price.store.market !== 'primary' && !showInternational) {
        return false;
      }

      // And then check the junk type
      switch (price.type) {
        case 'not_in_mint_condition':
          return showNotInMintCondition;
        case 'not_available_for_purchase':
          return showUnavailable;
        case 'used_product':
          return showUsedProducts;
        case 'pickup_only':
          return showPickupOnly;
        default:
          return true;
      }
    });
  }

  handlePriceFilterCheckbox(e) {
    this.setState({
      [e.target.name]: e.target.checked,
    });
  }

  getFilter() {
    const { prices } = this.props;

    if (!prices.nodes) {
      return {};
    }

    const hasInternationalPrices = prices.nodes.some(
      p => p.store.market !== 'primary'
    );
    const hasUsedProducts = prices.nodes.some(p => p.type === 'used_product');
    const hasNotInMintCondition = prices.nodes.some(
      p => p.type === 'not_in_mint_condition'
    );
    const hasPickupOnly = prices.nodes.some(p => p.type === 'pickup_only');
    const hasUnavailable = prices.nodes.some(
      p => p.type === 'not_available_for_purchase'
    );

    const hasFilter =
      hasInternationalPrices ||
      hasUsedProducts ||
      hasNotInMintCondition ||
      hasPickupOnly ||
      hasUnavailable;

    return {
      hasInternationalPrices,
      hasUsedProducts,
      hasNotInMintCondition,
      hasPickupOnly,
      hasUnavailable,
      hasFilter,
    };
  }

  static advertSpacing = { top: '10px', bottom: '10px' };

  // eslint-disable-next-line class-methods-use-this
  renderAds(currentPrices, adOffset) {
    if (!currentPrices) {
      return null;
    }
    const adsIndexes = [3, 9, 18, 36, 54, 72, 90, 108].map(
      index => index + adOffset
    );
    let numberOfAds = 0;
    adsIndexes.forEach(order => {
      if (currentPrices.length >= order) {
        numberOfAds += 1;
      }
    });
    if (currentPrices.length > 0) {
      numberOfAds = Math.max(1, numberOfAds);
    }
    const adContainerKeys = [];
    for (let i = 0; i < numberOfAds; i += 1) {
      adContainerKeys.push(`ad-container-${i}`);
    }

    return (
      <>
        <li className={`${styles.advert} with-position-1`}>
          <AdContainer
            placement="panorama_extra"
            min={860}
            max={10000}
            key="panorama_extra"
            scalable
            scaleBase=".pj-ui-pricelist--advert"
            spacing={PriceList.advertSpacing}
          />
        </li>
        {numberOfAds > 1 ? (
          <li className={`${styles.advert} with-position-2`}>
            <AdContainer
              placement="panorama_extra_2"
              min={860}
              max={10000}
              key="panorama_extra_2"
              scalable
              scaleBase=".pj-ui-pricelist--advert"
              spacing={PriceList.advertSpacing}
            />
          </li>
        ) : null}

        {adContainerKeys.map((key, index) => (
          <li
            className={styles.advert}
            style={{ order: adsIndexes[index] }}
            key={`mobile-${key}`}
          >
            <AdContainer
              placement={`dubbelpanorama${Math.min(3, index + 1)}`}
              min={0}
              max={860}
              spacing={PriceList.advertSpacing}
            />
          </li>
        ))}
      </>
    );
  }

  getDesktopAd = index => {
    const desktopAds = ['panorama_extra', 'panorama_extra_2'];
    const placement = desktopAds[Math.min(desktopAds.length - 1, index)];
    return (
      <tr
        className={`${styles.advert} with-position-1`}
        key={`desktop-ad-container-${index}`}
      >
        <td colSpan={String(columnCount)}>
          <AdContainer
            placement={placement}
            min={860}
            max={10000}
            key={`${placement}-${index}`}
            scalable
            scaleBase=".pj-ui-pricelist--advert"
            spacing={PriceList.advertSpacing}
          />
        </td>
      </tr>
    );
  };

  getMobileAd = index => {
    return (
      <tr className={styles.advert} key={`mobile-ad-container-${index}`}>
        <td colSpan={String(columnCount)}>
          <AdContainer
            placement={`dubbelpanorama${Math.min(3, index + 1)}`}
            min={0}
            max={860}
            spacing={PriceList.advertSpacing}
          />
        </td>
      </tr>
    );
  };

  insertTableAds(tableRows, adOffset) {
    const adsIndexes = [3, 9, 18, 36, 54, 72, 90, 108]
      .map(index => index + adOffset)
      .filter(index => index <= tableRows.length);
    if (!adsIndexes.length) {
      // special case for short list
      tableRows.splice(tableRows.length, 0, this.getDesktopAd(0));
      tableRows.splice(tableRows.length, 0, this.getMobileAd(0));
      return tableRows;
    }
    for (let i = adsIndexes.length - 1; i >= 0; i -= 1) {
      const index = adsIndexes[i];
      // Only include two desktop ads
      if (i <= 1) {
        tableRows.splice(index, 0, this.getDesktopAd(i));
      }
      tableRows.splice(index, 0, this.getMobileAd(i));
    }
    return tableRows;
  }

  lazyPlaceholderStyle = { height: 57 };

  lazyPlaceholder = (
    <tr>
      <td colSpan={String(columnCount)} style={this.lazyPlaceholderStyle} />
    </tr>
  );

  wrapLazy = (order, component) => {
    if (order > 10) {
      return (
        <Lazy placeholder={this.lazyPlaceholder} offset={200}>
          {component}
        </Lazy>
      );
    }
    return component;
  };

  renderPriceRow(item, index) {
    const { id, campaign, brand, showStoreProductNames } = this.props;
    const storeBadges = this.props.storeBadges
      .sort((a, b) => b.nrOfPositiveReviews - a.nrOfPositiveReviews)
      .reduce((acc, badge) => {
        acc[badge.itemId] = acc[badge.itemId] || [];
        acc[badge.itemId].push(badge);
        return acc;
      }, {});
    const { showInternational } = this.state;

    return this.wrapLazy(
      index,
      <PriceRow
        order={index + 1}
        key={item.id}
        productId={id}
        priceId={item.id}
        priceType={item.type}
        price={item.price}
        store={item.store}
        storeInfo={item.store.providedByStore.generalInformation}
        storeBadges={storeBadges[item.store.id]}
        stock={item.stock}
        externalUri={item.externalUri}
        brand={brand}
        alternativePrices={item.alternativePrices}
        openInNewWindow
        knownShippingPrice={!!item.price.inclShipping}
        brandRecommendedRetailer={item.brandRecommendedRetailer}
        isInternationalPrice={
          showInternational && item.store.market === 'secondary'
        }
        campaignStyling={
          campaign && campaign.price && item && item.id === campaign.price.id
            ? campaign.media.colors
            : null
        }
        onPriceClick={(featured, price, status) => {
          // NOTE: Any changes done to data or structure of PRODUCT_PRICE_CLICK needs to be
          // reflected in react/middlewares/out pre click for tracking clicks before JS bundle
          this.props.productPriceClick(id, price || item, featured, status);
        }}
        onClickOutBounce={this.props.productClickOutBounce}
        showStoreProductNames={showStoreProductNames}
      />
    );
  }

  static renderPlaceholders(count) {
    return [...new Array(count)].map((_val, index) => (
      <Placeholder key={`placeholder-${index.toString()}`} />
    ));
  }

  render() {
    const { t, market } = this.props.appContext;
    const {
      visiblePrices,
      productName,
      hideNonFeaturedStoreLinks,
      showStoreProductNames,
    } = this.props;
    const {
      showMorePrices,
      showUsedProducts,
      showInternational,
      showNotInMintCondition,
      showPickupOnly,
      showUnavailable,
      featuredAlternativePriceType,
      hiddenListOptions,
    } = this.state;

    const {
      hasInternationalPrices,
      hasUsedProducts,
      hasNotInMintCondition,
      hasPickupOnly,
      hasUnavailable,
      hasFilter,
    } = this.getFilter();

    const filteredPrices = this.getPrices();

    const priceFilters = [
      {
        name: 'showInternational',
        checked: showInternational,
        hasPrices: hasInternationalPrices,
        showAsQuickFilter: featuredAlternativePriceType === 'international',
        label: t('product', 'price-filter.international'),
        shortLabel: t('product', 'price-filter.international-short'),
        hide: false,
      },
      {
        name: 'showUsedProducts',
        checked: hasUsedProducts && showUsedProducts,
        hasPrices: hasUsedProducts,
        showAsQuickFilter: featuredAlternativePriceType === 'used_product',
        label: t('product', 'price-filter.used'),
        hide: false,
      },
      {
        name: 'showNotInMintCondition',
        checked: hasNotInMintCondition && showNotInMintCondition,
        label: t('product', 'price-filter.not-in-mint-condition'),
        hasPrices: hasNotInMintCondition,
        showAsQuickFilter: false,
        hide: Boolean(
          hiddenListOptions.filter(option => option === 'not_in_mint_condition')
            .length
        ),
      },
      {
        name: 'showPickupOnly',
        checked: hasPickupOnly && showPickupOnly,
        label: t('product', 'price-filter.pickup-only'),
        hasPrices: hasPickupOnly,
        showAsQuickFilter: false,
        hide: Boolean(
          hiddenListOptions.filter(option => option === 'pickup_only').length
        ),
      },
      {
        name: 'showUnavailable',
        checked: hasUnavailable && showUnavailable && true,
        label: t('product', 'price-filter.not-available-for-purchase'),
        hasPrices: hasUnavailable,
        showAsQuickFilter: false,
        hide: false,
      },
    ];

    const quickFilters = priceFilters.filter(elem => elem.showAsQuickFilter);

    // This experiment hides store link if store is non-featured.
    // 'externalUri' determines if link is shown or not.

    const shownPrices = showMorePrices
      ? filteredPrices
      : filteredPrices && filteredPrices.slice(0, visiblePrices);

    const currentPrices = hideNonFeaturedStoreLinks
      ? shownPrices.map(price =>
          price.store && price.store.featured
            ? price
            : { ...price, externalUri: null }
        )
      : shownPrices;

    return (
      <Panel
        disableTopMargin
        header={
          <div className={styles.header}>
            <PanelHeader
              title={
                <Fragment>
                  <span
                    className={styles['only-desktop']}
                    data-test="SortOrder"
                  >
                    {t('product', 'prices.title', { name: productName })}
                  </span>
                </Fragment>
              }
              divider="none"
            >
              <QuickPriceFilters
                priceFilters={quickFilters}
                onFilterChanged={this.handlePriceFilterCheckbox}
              />
              {hasFilter && (
                <Dropdown
                  title={
                    <Fragment>
                      <span className={styles['only-mobile']}>
                        <DotsVerticalIcon size={20} />{' '}
                      </span>
                      <span className={styles['only-desktop']}>
                        <T domain="product">prices.more-filters</T>{' '}
                      </span>
                    </Fragment>
                  }
                  type="custom-content-type"
                  headerPosition="right"
                  className={styles['price-filter']}
                  ariaLabel={t('product', 'prices.more-filters')}
                  data-test="PriceFilters"
                >
                  <PriceFilters
                    priceFilters={priceFilters}
                    onFilterChanged={this.handlePriceFilterCheckbox}
                  />
                </Dropdown>
              )}
            </PanelHeader>
          </div>
        }
        footer={
          <div>
            {market.showProductInformationTexts &&
              currentPrices &&
              currentPrices.length > 0 && (
                <div className={styles.information}>
                  <T domain="product">price-list.data-information</T>
                </div>
              )}
            {currentPrices && currentPrices.length < filteredPrices.length && (
              <Fragment>
                <Button
                  style={{ width: '100%' }}
                  iconPosition="bottom"
                  icon={<ChevronDown />}
                  variant="text"
                  color="link"
                  onClick={() => this.onMorePrices()}
                >
                  <T domain="product">prices.show-more-prices-button</T>
                </Button>
              </Fragment>
            )}
          </div>
        }
      >
        {this.props.prices.meta && (
          <PriceInformation
            storeStatistics={this.props.prices.meta.storeStatistics}
          />
        )}
        {currentPrices === null && PriceList.renderPlaceholders(5)}
        {currentPrices && currentPrices.length === 0 && (
          <div key={styles['no-prices']} className={styles['no-prices']}>
            <div>
              <T domain="product">prices.no-prices</T>
              {this.props.category && (
                <div>
                  <T domain="product">prices.view-all-in-category-link</T>{' '}
                  <Link to={this.props.category.pathName}>
                    {this.props.category.name}
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
        <S.PriceRowTable>
          <thead>
            <tr>
              <S.PriceRowTableHeadExpand />
              <S.PriceRowTableHeadStore scope="col">
                <div>{t('product', 'price-list.header-store')}</div>
              </S.PriceRowTableHeadStore>
              <S.PriceRowTableHeadProduct scope="col">
                <div>
                  {showStoreProductNames
                    ? t('product', 'price-list.header-product')
                    : ' '}
                </div>
              </S.PriceRowTableHeadProduct>
              <S.PriceRowTableHeadStock scope="col">
                <div>{t('product', 'price-list.header-stock')}</div>
              </S.PriceRowTableHeadStock>
              <S.PriceRowTableHeadPrice scope="col">
                <div>{t('product', 'price-list.header-price')}</div>
              </S.PriceRowTableHeadPrice>
              <S.PriceRowTableHeadClickOut />
            </tr>
          </thead>
          <tbody>
            {currentPrices &&
              currentPrices.length > 0 &&
              this.insertTableAds(
                currentPrices.map((item, index) =>
                  this.renderPriceRow(item, index)
                ),
                this.props.adOffset
              )}
          </tbody>
        </S.PriceRowTable>
      </Panel>
    );
  }
}

const PriceListWithExperiments = props => {
  const { showStoreProductNames } = useShowStoreProductNamesExperiment();
  const { hideNonFeaturedStoreLinks } = useNonFeaturedStoreLinkExperiment();

  return (
    <PriceList
      showStoreProductNames={showStoreProductNames}
      hideNonFeaturedStoreLinks={hideNonFeaturedStoreLinks}
      {...props}
    />
  );
};

const PriceListWithAppContext = props => {
  const appContext = useContext(AppContext);
  return <PriceListWithExperiments {...props} appContext={appContext} />;
};

PriceListWithAppContext.propTypes = {
  productPriceClick: PropTypes.func.isRequired,
  prices: graphqlPropTypes(PriceListFragment),
  visiblePrices: PropTypes.number,
  includeSecondary: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  campaign: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    type: PropTypes.string,
    tagline: PropTypes.string,
    description: PropTypes.shape({
      short: PropTypes.string,
      long: PropTypes.string,
      page: PropTypes.string,
    }),
    state: PropTypes.string,
    slug: PropTypes.string,
    web_uri: PropTypes.shape({
      mobile: PropTypes.string,
      desktop: PropTypes.string,
    }),
    media: PropTypes.shape({
      text: PropTypes.string,
      background: PropTypes.string,
    }),
    price: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  category: PropTypes.shape({
    pathName: PropTypes.string,
    name: PropTypes.string,
  }),
  brand: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    logo: PropTypes.string,
  }),
  storeBadges: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      sentiment: PropTypes.oneOf(['positive', 'negative']),
      nrOfPositiveReviews: PropTypes.number.isRequired,
      nrOfReviews: PropTypes.number.isRequired,
      itemId: PropTypes.number.isRequired,
    })
  ),
  productName: PropTypes.string.isRequired,
  productClickOutBounce: PropTypes.func.isRequired,
  adOffset: PropTypes.number,
};

PriceListWithExperiments.propTypes = {
  ...PriceListWithAppContext.propTypes,
  // Add appContext here pls
};

PriceList.propTypes = {
  ...PriceListWithExperiments.propTypes,
  showStoreProductNames: PropTypes.bool,
};

PriceListWithAppContext.defaultProps = {
  prices: {},
  visiblePrices: 50,
  brand: null,
  campaign: null,
  category: undefined,
  includeSecondary: false,
  storeBadges: [],
  adOffset: 0,
};

PriceListWithExperiments.defaultProps = PriceListWithAppContext.defaultProps;

PriceList.defaultProps = {
  ...PriceListWithAppContext.defaultProps,
  showStoreProductNames: false,
};

export default connect(
  state => state.areas.product,
  {
    productPriceClick,
    productClickOutBounce,
  }
)(PriceListWithAppContext);
